import {
  useState,
  useMemo,
  useCallback,
  useRef,
  useLayoutEffect,
} from 'react';
import ResizableTitle from './ResizableTitle';

/**
 * antd `<Table>` の列をマウスドラッグでリサイズ可能にするフック。
 * ------------------------------------------------------------------
 * ページ側の columns 配列を受け取り、各列に幅 state と `onHeaderCell` を
 * 注入して返す。`components`（ヘッダーセルを ResizableTitle 化）・`scroll`
 * （横スクロール幅）・`wrapRef`（幅計測用ラッパー ref）も返すので、ページは
 * `<Table>` をその ref を持つ div で包み、3 つを渡すだけでよい。
 *
 *   const { columns, components, scroll, wrapRef } = useResizableColumns(rawColumns);
 *   <div ref={wrapRef}>
 *     <Table columns={columns} components={components} scroll={scroll} ... />
 *   </div>
 *
 * オプション:
 *   - minWidth     : リサイズ下限（既定 60px）
 *   - maxWidth     : リサイズ上限（既定 1200px）
 *   - defaultWidth : width 未指定列の初期幅（既定 150px）
 *   - scroll       : `{ y? }`。縦スクロール（任意）。x は自動計算。
 *   - autoFit      : 初期表示でコンテナ幅いっぱいに列幅を広げるか（既定 true）。
 *
 * 列ごとの制御:
 *   - `resizable: false` を付けた列はリサイズ非対象（ハンドルなし）かつ
 *     autoFit の拡大対象からも除外（＝固定幅。アクション・アイコン列に使う）。
 *
 * 幅の挙動（重要）:
 *   ① 初期表示（autoFit=true）: ラッパーの実幅を計測し、全列幅 < コンテナ幅なら
 *      余白を「リサイズ可能な列」へ基準幅に比例配分して広げ、テーブルをコンテナ幅に
 *      ぴったり揃える（右側に余白を残さない）。全列幅 ≥ コンテナ幅（狭い画面）なら
 *      基準幅のまま＝横スクロール。ユーザーが一度でも列をドラッグしたら自動調整は停止。
 *   ② リサイズ: `ResizableTitle.css` の `min-width:0` でテーブル実幅 = 全列幅合計に
 *      固定。列を広げると合計が増えてテーブルが伸び、コンテナ幅を超えると横スクロール
 *      （他列の幅を奪わない＝各列独立 px）。狭めると縮む。
 *
 * 注意: 幅 state は初回マウント時の columns から初期化する（列構成が
 * 静的なページ前提）。列の出し入れを動的に行うページでは別途検討が要る。
 */
export default function useResizableColumns(rawColumns, options = {}) {
  const {
    minWidth = 60,
    maxWidth = 1200,
    defaultWidth = 150,
    scroll: scrollOption,
    autoFit = true,
    // rowSelection（チェックボックス）/ expandable（展開）など antd が自動で
    // 足す列の幅。autoFit の幅計算から差し引き、横スクロール幅にも加算する。
    reservedWidth = 0,
  } = options;

  // 列の安定キー（key → dataIndex → index の順で解決）
  const keyOf = (col, index) => col.key ?? col.dataIndex ?? index;
  const baseWidthOf = (col) =>
    typeof col.width === 'number' ? col.width : defaultWidth;

  // 列構成のシグネチャ（key:幅:可変 を連結）。再レンダーで rawColumns の
  // 参照が変わっても内容が同じなら不変＝下の effect の無限ループを防ぐ。
  const colsSignature = useMemo(
    () =>
      rawColumns
        .map(
          (c, i) =>
            `${keyOf(c, i)}:${baseWidthOf(c)}:${c.resizable === false ? 0 : 1}`
        )
        .join('|'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawColumns, defaultWidth]
  );

  // 幅 state（初回のみ基準幅で初期化）
  const [widths, setWidths] = useState(() => {
    const init = {};
    rawColumns.forEach((col, index) => {
      init[keyOf(col, index)] = baseWidthOf(col);
    });
    return init;
  });

  // ユーザーが手動リサイズしたら autoFit を止める
  const userResized = useRef(false);

  const handleResize = useCallback(
    (key) => (_e, { size }) => {
      userResized.current = true;
      setWidths((prev) => ({
        ...prev,
        [key]: Math.max(minWidth, Math.min(maxWidth, size.width)),
      }));
    },
    [minWidth, maxWidth]
  );

  // ラッパーの実幅を計測（コールバック ref）。
  // 要素がマウントされた時点で計測 + ResizeObserver を張るため、モーダル内
  // テーブルのように「後から表示される」表でも autoFit が正しく効く
  // （useRef + マウント時 1 回の effect だと、開いていないモーダルでは計測できない）。
  const [containerWidth, setContainerWidth] = useState(0);
  const roRef = useRef(null);
  const wrapRef = useCallback(
    (node) => {
      if (roRef.current) {
        roRef.current.disconnect();
        roRef.current = null;
      }
      if (node && autoFit) {
        setContainerWidth(node.clientWidth);
        if (typeof ResizeObserver !== 'undefined') {
          roRef.current = new ResizeObserver(() =>
            setContainerWidth(node.clientWidth)
          );
          roRef.current.observe(node);
        }
      }
    },
    [autoFit]
  );

  // 初期表示の自動フィット: コンテナ幅いっぱいに列を広げる（手動リサイズ前のみ）
  useLayoutEffect(() => {
    if (!autoFit || userResized.current || !containerWidth) return;
    // antd が足す列（チェックボックス等）の幅を差し引いた使用可能幅
    const avail = containerWidth - reservedWidth;
    const sumBase = rawColumns.reduce((s, c) => s + baseWidthOf(c), 0);
    // 狭い画面（全列幅 ≥ 使用可能幅）は基準幅のまま＝スクロール
    if (avail <= sumBase) {
      const base = {};
      rawColumns.forEach((c, i) => {
        base[keyOf(c, i)] = baseWidthOf(c);
      });
      setWidths(base);
      return;
    }
    // 余白を「リサイズ可能な列」へ基準幅に比例配分
    const slack = avail - sumBase;
    const flexible = rawColumns.filter((c) => c.resizable !== false);
    const sumFlexBase =
      flexible.reduce((s, c) => s + baseWidthOf(c), 0) || 1;
    const next = {};
    let added = 0;
    let lastFlexKey = null;
    rawColumns.forEach((c, i) => {
      const key = keyOf(c, i);
      if (c.resizable !== false) {
        const add = Math.round((slack * baseWidthOf(c)) / sumFlexBase);
        next[key] = baseWidthOf(c) + add;
        added += add;
        lastFlexKey = key;
      } else {
        next[key] = baseWidthOf(c);
      }
    });
    // 端数調整: 合計がコンテナ幅にちょうど一致するよう最後の可変列で吸収
    if (lastFlexKey != null) next[lastFlexKey] += slack - added;
    setWidths(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFit, containerWidth, colsSignature, reservedWidth]);

  const columns = useMemo(
    () =>
      rawColumns.map((col, index) => {
        const key = keyOf(col, index);
        const width = widths[key] ?? baseWidthOf(col);
        const resizable = col.resizable !== false;
        return {
          ...col,
          width,
          onHeaderCell: (column) => ({
            width: column.width,
            onResize: resizable ? handleResize(key) : undefined,
          }),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawColumns, widths, handleResize, defaultWidth]
  );

  // 横スクロール幅 = 全列幅の合計
  const totalWidth = useMemo(
    () => columns.reduce((sum, c) => sum + (typeof c.width === 'number' ? c.width : defaultWidth), 0),
    [columns, defaultWidth]
  );

  const components = useMemo(() => ({ header: { cell: ResizableTitle } }), []);

  // 横スクロール幅 = 全列幅 + antd が足す列（チェックボックス等）の予約幅
  const scroll = useMemo(
    () => ({ x: totalWidth + reservedWidth, ...(scrollOption || {}) }),
    [totalWidth, reservedWidth, scrollOption]
  );

  return { columns, components, scroll, wrapRef };
}
